const en = {
  nav: {
    home: "Home",
    services: "Services",
    blog: "Blog",
    about: "About Us",
    contact: "Contact Us",
  },
  hero: {
    title: "Your child's education in Egypt, handled from start to finish.",
    subtitle: "From school selection and paperwork to residency and accommodation — EduVate takes care of every step so you don't have to figure it out alone.",
    cta: "Book a Free Consultation",
  },
  homeIntro: {
    titlePrefix: "We've been doing this for over",
    titleHighlight: "10 years",
    description: "EduVate was built to make studying in Egypt straightforward for families coming from Jordan, Sudan, Yemen, and across the Arab world. We know the schools, the systems, the paperwork — and we know what parents worry about most. Our job is to take that weight off your shoulders.\n\nFrom the first call to the first day of school, we're with you at every step.",
    cta: "Learn More About Us",
  },
  homeStats: {
    items: [
      { valueFirst: "10+", labelAfter: "Years of Experience" },
      { valueFirst: "1,000+", labelAfter: "Students Enrolled" },
      { valueFirst: "20+", labelAfter: "Educational Partners" },
      { labelBefore: "Students from", valueFirst: "20+ Countries" },
    ],
  },
  homeServices: {
    title: "Everything in one place.",
    description: "We handle the full journey — from choosing the right curriculum to your child's first week of school.",
    cta: "Explore All Services",
    items: [
      {
        title: "School Selection & Academic Advising",
        description: "We help you choose the right school and curriculum based on your child's grade, academic background, and your family's goals.",
      },
      {
        title: "Admission & Paperwork",
        description: "We handle all required documents, school applications, and follow-ups with Egyptian educational authorities until acceptance is confirmed.",
      },
      {
        title: "Residency Management",
        description: "We guide families through Egypt's residency process step by step — including what documents are needed and when to come.",
      },
      {
        title: "Accommodation & Transport",
        description: "We help arrange suitable accommodation close to the school and support your child's daily logistics so they can focus on studying.",
      },
      {
        title: "Ongoing Student Support",
        description: "We don't disappear after enrollment. Our team stays reachable throughout the school year — for students and parents alike.",
      },
    ],
  },
  homeWhyEgypt: {
    title: "Why Egypt?",
    description: "Egypt is one of the Arab world's leading education destinations — and for good reason.",
    cta: "Explore All Services",
    items: [
      {
        icon: "curriculum",
        title: "Recognized Curricula",
        description: "Egyptian schools offer three internationally recognized tracks: the National system (Arabic and Languages), the British IG system, and the American Diploma. All are accepted by universities regionally and globally.",
      },
      {
        icon: "home",
        title: "Close to Home",
        description: "Egypt is a short flight from Jordan, Sudan, Yemen, and most Arab countries. Families can visit easily, and students don't feel entirely far from home.",
      },
      {
        icon: "arabic",
        title: "Arabic-Language Environment",
        description: "For Arab students, Egypt offers a familiar language and culture. The transition is easier here than it would be in a Western country.",
      },
      {
        icon: "affordable",
        title: "Affordable Quality",
        description: "Quality schooling in Egypt costs considerably less than comparable programs in Europe or the Gulf — without compromising on the level of education.",
      },
      {
        icon: "support",
        title: "Safe and Supported",
        description: "With EduVate by your side, your child isn't navigating a foreign country alone. We help arrange accommodation, transport, and support so parents can feel at ease.",
      },
    ],
  },
  homeCurriculumPaths: {
    titleHighlight: "Three",
    title: "curriculum paths. We'll help you choose the right one.",
    footerText: "Not sure which fits your child? Our advisors will help you figure it out.",
    cta: "Get a Free Curriculum Consultation",
    items: [
      {
        title: "National System (Arabic / Languages Track)",
        description:
          "Best for students who want full integration into the Egyptian school system. Taught in Arabic, with a language specialization option.",
        image: "/images/curriculum-national.png",
        imagePosition: "center center",
      },
      {
        title: "British IG System",
        description:
          "One of the most widely recognized international curricula. Students study 8 subjects over 2–3 years and can sit for exams more than once to improve their grades. Accepted by universities worldwide.",
        image: "/images/curriculum-british.png",
        imagePosition: "center center",
      },
      {
        title: "American Diploma",
        description:
          "A credit-hour based system where students progress at their own pace. Globally recognized — including the SAT, which is one of the highest-level qualifications for higher education.",
        image: "/images/curriculum-american.png",
        imagePosition: "center center",
      },
    ],
  },
  featuredCourses: {
    title: "Featured Courses",
    enroll: "Enroll",
    by: "by",
  },
  homeSupportIntro: {
    title: "A decision this big deserves\nthe right support.",
    description:
      "Moving your child to a new country to study is not a small thing. It takes planning, paperwork, and a lot of trust. We built EduVate because we know how stressful that process can be when you're doing it alone.",
  },
  homeCtaBanner: {
    title: "Education put in the right hands.",
    description:
      "The right guidance that helps students find the right track, build their learning journey, and excel.",
    primaryCta: "Get in Touch",
    secondaryCta: "About Us",
    image: "/images/quilia-1-aA2Fadydc-unsplash.jpg",
  },
  whyUs: {
    title: "Why Choose Eduvate?",
    items: [
      { title: "Expert Instructors", description: "Learn from industry professionals with real-world experience." },
      { title: "Flexible Learning", description: "Study at your own pace, anytime and anywhere." },
      { title: "Affordable Pricing", description: "Quality education that fits your budget." },
      { title: "Lifetime Access", description: "Access your course materials forever." },
    ],
  },
  services: {
    title: "Everything you need. Nothing left out.",
    subtitle: "We provide a wide range of educational services to help you succeed.",
    cta: "Explore our Services",
    items: [
      {
        title: "EduVate Advisor",
        description:
          "Our advisors help families understand what's possible based on their child's grade, curriculum background, and academic record — then recommend the best path forward.",
      },
      {
        title: "EduVate Consultant",
        description:
          "Our education consultants research specific schools and programs tailored to your child's profile, so you're not applying blindly.",
      },
      {
        title: "Admission Management",
        description:
          "We handle all paperwork, school applications, and follow-up with Egyptian educational authorities until the official acceptance letter is in your hands.",
      },
      {
        title: "Residency Management",
        description:
          "Navigating Egyptian residency is complicated for first-timers. Our team works with experienced partners to handle every step for students and accompanying guardians.",
      },
      {
        title: "Accommodation & Transport",
        description:
          "We help arrange suitable housing close to the school and support transportation needs — so your child has a safe, stable routine from day one.",
      },
      {
        title: "Student Support System",
        description:
          "From the first week to the final exam, our team stays available. Parents and students can reach us whenever they need help.",
      },
    ],
  },
  servicesHero: {
    title: "The full study abroad journey,\nhandled for you.",
    subtitle:
      "EduVate covers everything a family needs to get their child into school in Egypt — from the first conversation to graduation.",
    image: "/images/services-hero-students.png",
  },
  servicesWhy: {
    title: "Why EduVate?",
    description:
      "We believe every student deserves access to quality education and every family deserves to feel confident about the decision they're making.\n\nWe don't rush families into choices. We take the time to understand each student's situation, and we stay involved long after the enrollment is done. Every student we work with is treated with the same care and seriousness we'd want for our own children.",
    image: "/images/support-intro-graduate.jpg",
  },
  servicesFaq: {
    title: "Common questions from parents and students.",
    description:
      "Before you start the process, it's natural to have a lot of questions. Here are the ones we hear most often with straightforward answers from our admissions team.",
    items: [
      {
        question: "What documents are needed to apply?",
        answer:
          "The required documents include: a birth certificate authenticated by the embassy and the foreign affairs authority of the student's country, 8 personal photos with the student's full name in Arabic written below each one, a valid ID for both the student and guardian, residency documents for the student and guardian, an entry stamp on the student's passport, and the last 3 school certificates (certified).\n\nSome schools may also require a placement test or interview.",
      },
      {
        question: "Does the student need to come to Egypt in person?",
        answer:
          "Yes. The student and accompanying guardian must be present in Egypt to complete the residency process and have the passport stamped at the relevant authority. If the father is unable to travel, a formal educational guardianship or power of attorney for a designated guardian is required.",
      },
      {
        question: "How long does the full process take once the student arrives?",
        answer:
          "On average, the process takes around two weeks — provided all documents are complete and properly certified before arrival.",
      },
      {
        question: "Can EduVate help arrange accommodation during the process?",
        answer:
          "Accommodation options depend on the student's situation and budget. We help families identify suitable options close to the school.",
      },
      {
        question: "Is studying online or in-person?",
        answer:
          "Both options are available.\n\nOnline (Home School): Lower cost. The student studies remotely and is not required to attend school daily.\n\nOffline (In-Person): The student attends school and sits exams in person. This option costs more, as it requires accommodation and transport — but it provides a fuller school experience.",
      },
      {
        question: "What happens after documents are submitted?",
        answer:
          "Once we receive complete and certified documents, we begin the following steps:\n\nContact the school where the student will be enrolled based on their location.\nVisit the Egyptian Educational Administration to get official approval and the required institutional stamp.\nRegister the student online with the school (placement depends on capacity and grade level).\nFor students transferring from another country, curriculum equivalency must be processed through the Educational Administration.",
      },
      {
        question: "Can I choose a specific school or area?",
        answer:
          "We are not able to guarantee a specific school upfront, as placement depends on school capacity and grade availability. Once we assess the options based on those factors, we will notify you of the available choices and recommend the best fit.",
      },
      {
        question: "What curricula are available?",
        answer:
          "Students can choose between:\n\nNational System — Arabic track or Languages track\nBritish IG System — Students study 8 subjects and can sit exams over 2–3 years. The system is fully recognized in Egypt from Grade 1 to Grade 12.\nAmerican Diploma — A credit-hour system that is recognized in Egypt and internationally. Students may complete the program early if they finish required hours ahead of schedule.\nIB (International Baccalaureate) — The most academically demanding option, equivalent to the American Diploma in terms of university entry.",
      },
      {
        question: "What is the best time to apply?",
        answer:
          "We recommend starting the process between July and November. School places fill up early, and starting sooner gives your child more options.",
      },
      {
        question: "Can we apply from outside Egypt?",
        answer:
          "Yes. We handle the full application and communication process remotely. You don't need to travel until it's time to finalize residency.",
      },
    ],
  },
  blog: {
    title: "A place to learn before you decide.",
    subtitle: "Articles, guides, and advice for families navigating education in Egypt — written by people who've been through it with thousands of students.",
    readMore: "Read More",
    posts: [
      { title: "10 Tips for Effective Online Learning", date: "April 15, 2026", excerpt: "Discover proven strategies to make the most of your online education experience." },
      { title: "The Future of EdTech in 2026", date: "April 10, 2026", excerpt: "Exploring the latest trends and technologies shaping the education industry." },
      { title: "How to Stay Motivated While Studying", date: "April 5, 2026", excerpt: "Practical advice to keep your motivation high throughout your learning journey." },
      { title: "Building a Career in Tech", date: "March 28, 2026", excerpt: "A comprehensive guide to launching your career in the technology sector." },
    ],
  },
  about: {
    title: "About Eduvate",
    mission: {
      title: "Our Mission",
      p1: "At Eduvate, we believe education should be accessible to everyone, everywhere. We're committed to providing high-quality, affordable online courses that help learners achieve their goals.",
      p2: "Our platform brings together expert instructors and passionate learners to create a thriving community of lifelong learners.",
    },
    whyUs: {
      title: "Why Choose Us?",
      items: [
        "Expert instructors with real-world experience",
        "Flexible learning schedules",
        "Affordable pricing",
        "Lifetime access to course materials",
      ],
    },
    stats: {
      students: "Students",
      courses: "Courses",
      instructors: "Instructors",
      countries: "Countries",
    },
  },
  aboutHero: {
    title: "Where education dreams come true.",
    subtitle:
      "10+ years helping Arab students build their future in Egypt.\nWe've guided thousands of students through the full journey — and we're still at it.",
    image: "/images/about-hero-classroom.png",
  },
  aboutVisionMission: {
    image: "/images/support-intro-graduate.jpg",
    visionTitle: "VISION",
    visionParagraphs: [
      "To be the trusted partner that connects Arab students with quality educational opportunities in Egypt — helping them grow academically and personally while finding their footing in a new environment.",
      "We believe every student deserves access to the right education, guided by people who actually care about the outcome.",
    ],
    missionTitle: "MISSION",
    missionParagraphs: [
      "We guide students through every step of the journey — from school selection and paperwork to enrollment and their first week of classes.",
      "We carefully match each student to the right school and curriculum based on their academic background and family goals. We handle the procedures. We arrange the logistics. And we stay available throughout the year, not just until the acceptance letter arrives.",
    ],
  },
  contact: {
    title: "We're Here to Help — Get in Touch Anytime",
    introTitle: "We're Here to Help —\nGet in Touch Anytime",
    introDescription:
      "Have a question, need support, or want to speak with our team? Fill out the form below or reach us directly. We respond quickly, and the first consultation is always free.",
    cardTitle: "Contact Details",
    followUs: "Follow us",
    formLeadTitle: "Tell us about your child.",
    formLeadSubtitle:
      "Fill out the form below and one of our advisors will reach out to you as soon as possible.",
    form: {
      name: "Name",
      namePlaceholder: "Your Name",
      email: "Email",
      emailPlaceholder: "Your Email",
      message: "Message",
      messagePlaceholder: "Your Message",
      send: "Send Message",
      sending: "Sending...",
      success: "Thank you! Your message has been sent successfully.",
      error: "Something went wrong. Please check your connection and try again.",
    },
    formFields: {
      studentName: "Student name",
      studentNamePlaceholder: "Type your Student name",
      email: "Email",
      emailPlaceholder: "Type your email",
      gender: "Gender",
      birthDate: "Birth date",
      phoneNumber: "Phone number",
      phoneNumberPlaceholder: "00 000 0000",
      school: "School",
      schoolPlaceholder: "Type your School name",
      grade: "Grade",
      gradePlaceholder: "Type your grade",
      city: "City",
      cityPlaceholder: "Type your city",
      studentCategory: "Student category",
      egyptian: "Egyptian",
      international: "International",
      nonEgyptian: "Non-Egyptian",
      educationSystem: "Education system",
      educationSystemPlaceholder: "Type the education system",
      firstLanguage: "First language",
      firstLanguagePlaceholder: "Type the first language",
      secondLanguage: "Second language",
      secondLanguagePlaceholder: "Type the second language",
      lastGrade: "Last grade",
      lastGradePlaceholder: "Type your last grade",
      selectPlaceholder: "Suggestion",
      male: "Male",
      female: "Female",
      newStudent: "New student",
      transferStudent: "Transfer student",
      nationalSystem: "National System",
      britishSystem: "British IG",
      americanSystem: "American Diploma",
      ibSystem: "IB",
      arabic: "Arabic",
      english: "English",
      french: "French",
      submit: "Send My Details",
    },
    info: {
      email: "Email",
      emailValue: "info@cvis.com.eg",
      phone: "Phone",
      phoneValue: "01240079406 / 01240079407",
      address: "Our Location",
      addressValue: "Building No. 129, Tahrir Street, Dokki — Giza",
      officeHours: "Office Hours",
      officeHoursValue: "Sunday - Thursday, 8:00 AM - 4:00 PM",
    },
  },
  footer: {
    company: "Company",
    about: "Who We Are",
    aboutText: "Education put in the right hands.",
    quickLinks: "Quick Links",
    contactTitle: "Contact",
    rights: "Eduvate. All rights reserved.",
    rightsFull: "© 2022 Welcome. All right reserved.",
    privacy: "Privacy Policy",
    terms: "Terms of Service",
  },
};

export default en;
